import React, { useCallback } from 'react'
import { Marker, useMap } from 'react-leaflet'
import { useState } from 'react'
import { BBox } from '@turf/turf'
import L, { LatLngTuple } from 'leaflet'
import { MapMarker, MapMarkerProps } from './MapMarker/MapMarker'
import useSupercluster from 'use-supercluster'

type ClusterProperties = {
  cluster: boolean
  point_count?: number
  mapMarker?: MapMarkerProps
}

const OFFSET = 0.00005
const adjustOverlappingMarkers = (
  latitude: number,
  longitude: number,
  index: number,
): [number, number] => {
  const angle = index * 45 * (Math.PI / 180)
  const offsetLat = latitude + OFFSET * Math.sin(angle)
  const offsetLng = longitude + OFFSET * Math.cos(angle)
  return [offsetLat, offsetLng]
}

const fetchIcon = (count: number, size: number) => {
  const colors =
    count > 100
      ? { background: 'hsla(18,97%,72%,.6)', shadow: 'hsla(18,97%,72%,.6)' }
      : count > 20
        ? { background: '#f0c20c99', shadow: '#f1d35799' }
        : { background: '#6ecc3999', shadow: 'rgba(181, 226, 140, 0.6)' }

  return L.divIcon({
    html: `<div style="width: ${size}px; height: ${size}px;   
    padding: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: black;
    background: ${colors.background};
    box-shadow:0 0 0 4px ${colors.shadow};">
      ${count}
    </div>`,
  })
}

export const MarkersWithSuperCluster: React.FC<{ data: MapMarkerProps[] }> = ({ data }) => {
  const maxZoom = 22
  const [zoom, setZoom] = useState<number>(16)
  const [mapBounds, setMapBounds] = useState<number[]>([0, 0, 0, 0])
  const map = useMap()

  const updateMap = useCallback(() => {
    if (!map) {
      return
    }

    const bounds = map
      .getBounds()
      .toBBoxString()
      .split(',')
      .map((str) => parseFloat(str))
    const currentZoom = map.getZoom()

    setMapBounds(bounds)
    setZoom(currentZoom)
  }, [map])

  React.useEffect(() => {
    if (!map) return

    // Initial update
    updateMap()

    // Event handler
    const handleMove = () => {
      updateMap()
    }

    map.on('moveend', handleMove)

    return () => {
      map.off('moveend', handleMove)
    }
  }, [map, updateMap])

  const points: GeoJSON.Feature<GeoJSON.Point, ClusterProperties>[] = data.map((item) => ({
    type: 'Feature',
    properties: { cluster: false, mapMarker: item },
    geometry: {
      type: 'Point',
      coordinates: [
        (item.marker.position as LatLngTuple)[1],
        (item.marker.position as LatLngTuple)[0],
      ],
    },
  }))

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: mapBounds as BBox,
    zoom,
    options: { radius: 40, maxZoom: 17 },
  })

  const sameLocationGroups = React.useMemo(() => {
    const groups: { [key: string]: GeoJSON.Feature<GeoJSON.Point, ClusterProperties>[] } = {}

    clusters.forEach((cluster) => {
      if (cluster.properties.cluster) {
        return
      }

      const key = `${cluster.geometry.coordinates[0]},${cluster.geometry.coordinates[1]}`
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(cluster)
    })

    return groups
  }, [clusters])

  if (!clusters || supercluster === undefined || !map) return null

  return clusters.map((cluster) => {
    const [longitude, latitude] = cluster.geometry.coordinates
    const { cluster: isCluster, point_count: pointCount } = cluster.properties

    if (isCluster && pointCount) {
      return (
        <Marker
          key={`cluster-${cluster.id}`}
          position={[latitude, longitude]}
          icon={fetchIcon(pointCount, 10 + (pointCount / points.length) * 40)}
          zIndexOffset={1000}
          pmIgnore={true}
          eventHandlers={{
            click: () => {
              const expansionZoom = Math.min(
                supercluster.getClusterExpansionZoom(cluster.id as number),
                maxZoom,
              )
              map.setView([latitude, longitude], expansionZoom + 2, {
                animate: true,
              })
            },
          }}
        />
      )
    }

    const key = `${longitude},${latitude}`
    const isOverlapping = sameLocationGroups[key]?.length > 1

    const adjustedPosition = isOverlapping
      ? adjustOverlappingMarkers(
          latitude,
          longitude,
          sameLocationGroups[key].findIndex((c) => c === cluster),
        )
      : [latitude, longitude]

    return (
      <MapMarker
        key={`point-${cluster.properties.mapMarker.id}`}
        {...cluster.properties.mapMarker}
        marker={{
          ...cluster.properties.mapMarker.marker,
          position: adjustedPosition,
        }}
      />
    )
  })
}
